import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../../hooks/Authorize";
import ProgressBar from "../ProgressBar";
import { useUserProfile } from "../../context/UserProfileContext";
import CreateFolderDialog from "../Dialogs/CreateFolderDialog";
import StorageFullPopupDialog from "../Dialogs/StorageFullPopupDialog";

const AddResourceMenu = ({
  setClikkleItems,
  clikkleItems,
  anchorEl,
  onClose,
}) => {
  const [showProcessingSnackbar, setShowProcessingSnackbar] = useState(false);
  const [processingMessage, setProcessingMessage] = useState(false);
  const platformUser = JSON.parse(localStorage.getItem("user"));
  const location = useLocation();
  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);

  var isViewerScreen = location.pathname === "/view";

  const { fsUserProfile, setFsUserProfile } = useUserProfile();
  const [userTotalSpacePlan, setUserTotalSpacePlan] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);

  const [isSpaceFull100ModalOpen, setIsSpaceFull100ModalOpen] = useState(false);

  const getFsUserProfile = useCallback(async () => {
    try {
      setIsLoadingProfile(true);
      const response = await axios.get(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/user/${platformUser._id}`
      );
      setFsUserProfile(response.data);
      console.log("setFsUserProfile = ", response.data);
    } catch (error) {
      console.error("Profile Fetch Error:", error);
    } finally {
      setIsLoadingProfile(false);
    }
  }, [platformUser._id, setFsUserProfile]);

  const checkSubscriptionDetails = useCallback(async () => {
    try {
      setIsLoadingSubscription(true);
      const response = await axios.get(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/subscription/${platformUser._id}`
      );

      const data = response.data;
      setUserTotalSpacePlan(data?.permissions);
    } catch (error) {
      console.log("API Error:", error?.response?.data);
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [platformUser._id]);

  useEffect(() => {
    getFsUserProfile();
    checkSubscriptionDetails();
  }, [getFsUserProfile, checkSubscriptionDetails]);

  // Storage calculation logic
  const BYTES_IN_GB = 1073741824;
  const BYTES_IN_MB = 1048576;

  const noOfAllowed = userTotalSpacePlan?.noOfAllowed ?? 5;
  const format = userTotalSpacePlan?.format ?? "GB";
  const isUnlimited = userTotalSpacePlan?.isUnlimited ?? false;

  let TOTAL_SPACE_GB = 0;
  let percentageUsed = 0;
  let isSpaceFull100 = false;

  if (!isUnlimited) {
    if (format === "TB") {
      TOTAL_SPACE_GB = noOfAllowed * 1024;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedGB = usedBytes / BYTES_IN_GB;
      percentageUsed = (usedGB / TOTAL_SPACE_GB) * 100;
    } else if (format === "GB") {
      TOTAL_SPACE_GB = noOfAllowed;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedGB = usedBytes / BYTES_IN_GB;
      percentageUsed = (usedGB / TOTAL_SPACE_GB) * 100;
    } else if (format === "MB") {
      TOTAL_SPACE_GB = noOfAllowed;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedMB = usedBytes / BYTES_IN_MB;
      percentageUsed = (usedMB / TOTAL_SPACE_GB) * 100;
    }

    isSpaceFull100 = percentageUsed >= 90;
  }

  const handleNewFolder = () => {
    if (isSpaceFull100) {
      setIsSpaceFull100ModalOpen(true);
      return;
    }
    onClose("AddResourceMenu");
    setTimeout(() => setOpenCreateFolderDialog(true), 0);
  };

  const handleFileUpload = async (event) => {
    if (isSpaceFull100) {
      setIsSpaceFull100ModalOpen(true);
      return;
    }
    if (!event.target.files?.length) return;

    const file = event.target.files[0];
    setProcessingMessage("Uploading file...");
    setShowProcessingSnackbar(true);

    const formData = new FormData();
    formData.append("ownerId", platformUser._id);
    formData.append("type", "file");
    formData.append("file", file);
    formData.append("ownerEmail", platformUser.email);

    const parentId = new URLSearchParams(window.location.search).get(
      "parentId"
    );
    if (parentId && !isViewerScreen) {
      formData.append("parentId", parentId);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/fs/create?id=${platformUser._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (!isViewerScreen) {
        setClikkleItems([...clikkleItems, response.data.record]);

        setFsUserProfile((prevProfile) => ({
          ...prevProfile,
          usedSpace: response.data.usedSpace,
        }));
      }
      console.log("file create response = ", response);
    } catch (error) {
      console.error("Upload Failed:", error);
    } finally {
      setShowProcessingSnackbar(false);
      setProcessingMessage("");
      try {
        event.target.value = "";
      } catch {}
    }
  };

  const folderInputRef = useRef(null);

  const triggerFolderUpload = () => {
    if (isSpaceFull100) {
      setIsSpaceFull100ModalOpen(true);
      return;
    }
    console.log("[DEBUG] Triggering folder upload...");
    onClose("AddResourceMenu");

    const input = document.createElement("input");
    input.type = "file";
    input.style.display = "none";
    input.webkitdirectory = true;
    input.directory = true;
    input.multiple = true;

    input.onchange = (e) => {
      console.log("[DEBUG] Dynamic input onChange triggered");
      handleFolderUpload(e);
      document.body.removeChild(input);
    };

    document.body.appendChild(input);
    input.click();
  };

  const handleFolderUpload = async (event) => {
    if (isSpaceFull100) {
      setIsSpaceFull100ModalOpen(true);
      return;
    }
    console.log("[DEBUG] handleFolderUpload called with:", event);

    if (!event.target.files?.length) {
      console.error("[DEBUG] No files selected");
      return;
    }

    const files = Array.from(event.target.files);
    console.log("[DEBUG] Files to upload:", files);

    setShowProcessingSnackbar(true);
    setProcessingMessage("Uploading folder...");

    try {
      const formData = new FormData();
      formData.append("ownerId", platformUser._id);
      formData.append("type", "folder");
      formData.append("ownerEmail", platformUser.email);

      const parentId = new URLSearchParams(window.location.search).get(
        "parentId"
      );
      if (parentId && !isViewerScreen) {
        formData.append("parentId", parentId);
      }

      files.forEach((file, index) => {
        formData.append("files", file);
        formData.append(`relativePaths[${index}]`, file.webkitRelativePath);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/fs/upload-folder?id=${platformUser._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      console.log("[DEBUG] Upload success:", response.data);

      if (!isViewerScreen) {
        setClikkleItems((prevItems) => {
          const exists = prevItems.some(
            (item) => item._id === response.data._id
          );
          if (!exists) {
            return [...prevItems, response.data.record];
          }
          return prevItems;
        });

        setFsUserProfile((prevProfile) => ({
          ...prevProfile,
          usedSpace: response.data.usedSpace,
        }));
      }
    } catch (error) {
      console.error("[DEBUG] Upload failed:", error);
    } finally {
      setShowProcessingSnackbar(false);
      setProcessingMessage("");
      if (event.target) event.target.value = "";
    }
  };

  return (
    <>
      <ProgressBar
        showProcessingSnackbar={showProcessingSnackbar}
        processingMessage={processingMessage}
      />

      <CreateFolderDialog
        open={openCreateFolderDialog}
        onClose={() => setOpenCreateFolderDialog(false)}
        setClikkleItems={setClikkleItems}
        clikkleItems={clikkleItems}
        setShowProcessingSnackbar={setShowProcessingSnackbar}
        setProcessingMessage={setProcessingMessage}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => onClose("AddResourceMenu")}
        PaperProps={{
          sx: {
            backgroundColor: "rgb(3, 3, 3)",
            color: "#fff",
            borderRadius: "8px",
            minWidth: "250px",
            padding: "4px 0",
          },
        }}
      >
        <MenuItem onClick={handleNewFolder}>
          <ListItemIcon>
            <CreateNewFolderOutlinedIcon sx={{ color: "#fff" }} />
          </ListItemIcon>
          <ListItemText primary="New Folder" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (isSpaceFull100) {
              setIsSpaceFull100ModalOpen(true);
              return;
            }
            onClose("AddResourceMenu");
            const fileInput = document.getElementById("fileUploadInput");
            fileInput.onchange = (e) => {
              const file = e.target.files[0];
              if (file) {
                handleFileUpload(e);
                console.log("Selected file:", file.name);
              }
            };
            fileInput.click();
          }}
        >
          <ListItemIcon>
            <UploadFileOutlinedIcon sx={{ color: "#fff" }} />
          </ListItemIcon>
          <ListItemText primary="File Upload" />
        </MenuItem>
        <input type="file" id="fileUploadInput" style={{ display: "none" }} />

        <MenuItem onClick={triggerFolderUpload}>
          <ListItemIcon>
            <DriveFolderUploadOutlinedIcon sx={{ color: "#fff" }} />
          </ListItemIcon>
          <ListItemText primary="Folder Upload" />
        </MenuItem>

        <input
          type="file"
          id="folderUploadInput"
          ref={folderInputRef}
          style={{ display: "none" }}
          webkitdirectory=""
          directory=""
          multiple
          onChange={handleFolderUpload}
        />
      </Menu>

      {/* Only show popup if storage is actually 100% full */}
      {isSpaceFull100 && (
        <StorageFullPopupDialog
          isSpaceFull100ModalOpen={isSpaceFull100ModalOpen}
          isSpaceFull100={isSpaceFull100}
          is100Full={isSpaceFull100}
          setIsSpaceFull100ModalOpen={setIsSpaceFull100ModalOpen}
          isUnlimited={isUnlimited}
          dialogLoading={isLoadingProfile || isLoadingSubscription}
          noOfAllowed={noOfAllowed}
          format={format}
        />
      )}
    </>
  );
};

export default AddResourceMenu;